import React from 'react';
import { FaCheckCircle, FaGraduationCap, FaUsers, FaTrophy, FaHeart, FaBookOpen, FaSchool, FaLeaf } from 'react-icons/fa';
import { GiAchievement, GiTeacher } from 'react-icons/gi';
import AboutImage from '../../assets/images/17316.jpg';

const AboutSection = ({ imageSide = 'left' }) => {
  const features = [
    { icon: <FaGraduationCap className="w-4 h-4" />, text: "CBSE Affiliated School", color: "text-blue-600" },
    { icon: <FaUsers className="w-4 h-4" />, text: "Experienced Faculties", color: "text-emerald-600" },
    { icon: <FaBookOpen className="w-4 h-4" />, text: "Holistic Curriculum", color: "text-purple-600" },
    { icon: <GiAchievement className="w-4 h-4" />, text: "31+ Years Excellence", color: "text-amber-600" },
    { icon: <FaHeart className="w-4 h-4" />, text: "Nurturing Environment", color: "text-rose-600" },
    { icon: <FaTrophy className="w-4 h-4" />, text: "Focus on Sports & Arts", color: "text-indigo-600" },
  ];

  const stats = [
    { number: "100%", label: "Board Results 2025" },
    { number: "10K+", label: "Happy Students" },
    { number: "50+", label: "Expert Faculties" },
    { number: "31+", label: "Years Experience" },
  ];

  const additionalInfo = [
    {
      icon: <FaSchool className="w-4 h-4" />,
      title: "Modern Infrastructure",
      description: "State-of-the-art classrooms, labs, and sports facilities",
      color: "bg-blue-50 text-blue-600"
    },
    {
      icon: <FaLeaf className="w-4 h-4" />,
      title: "Green Campus",
      description: "Eco-friendly environment with gardens and solar power",
      color: "bg-emerald-50 text-emerald-600"
    }
  ];

  return (
    <section className="py-8 bg-white">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className={`flex flex-col ${imageSide === 'left' ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-start gap-6 lg:gap-8`}>
          
          {/* Image Side */}
          <div className="w-full lg:w-1/2">
            <div className="relative">
              {/* Main Image */}
              <div className="overflow-hidden rounded-lg shadow-md">
                <img
                  src={AboutImage}
                  alt="Gyan Rashmi School Campus"
                  className="object-cover w-full h-auto transition-transform duration-300 hover:scale-102"
                />
              </div>
              
              {/* Additional Info Cards below Image */}
              <div className="grid grid-cols-1 gap-3 mt-4 md:grid-cols-2">
                {additionalInfo.map((item, index) => (
                  <div 
                    key={index}
                    className={`p-3 rounded-lg border ${item.color.split(' ')[0]} border-gray-100`}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`p-1.5 rounded-md ${item.color}`}>
                        {item.icon}
                      </div>
                      <div className="flex-1">
                        <h4 className="mb-0.5 text-xs font-semibold text-gray-800">{item.title}</h4>
                        <p className="text-xs leading-tight text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Floating Stats Card */}
              
            </div>
          </div>

          {/* Content Side */}
          <div className="w-full lg:w-1/2">
            <div className="max-w-2xl">
              {/* Section Badge */}
              <div className="inline-flex items-center px-2.5 py-0.5 mb-2 text-xs font-medium text-blue-600 rounded-full bg-blue-50">
                <span className="w-1.5 h-1.5 mr-1.5 bg-blue-500 rounded-full"></span>
                About Our School
              </div>
              
              {/* Main Heading */}
              <h2 className="mb-3 text-xl font-bold text-gray-900 md:text-2xl">
                Gyan Rashmi Siksha Sadan 
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600"> (1994)</span>
              </h2>
              
              {/* Description */}
              <div className="mb-4 space-y-2 text-gray-600">
                <p className="text-sm leading-relaxed">
                  Welcome to Gyan Rashmi Siksha Sadan, where education meets excellence. 
                  Established in 1994, we have been at the forefront of providing quality 
                  education that blends traditional values with modern teaching methodologies.
                </p>
                <p className="text-sm leading-relaxed">
                  Our mission is to create a nurturing environment where every student 
                  can discover his/her potential, develop critical & innovative thinking skills, and 
                  grow into responsible global citizen.
                </p>
                <p className="text-sm leading-relaxed">
                  We believe in holistic development, combining academic rigor with 
                  co-curricular activities to ensure well-rounded growth for every child.
                </p>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-1.5 p-1.5 rounded-md bg-gray-50">
                    <div className={`p-1 rounded ${feature.color} bg-white border border-gray-100`}>
                      {feature.icon}
                    </div>
                    <span className="flex-1 text-xs font-medium text-gray-700">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 gap-3 p-4 mb-4 border border-blue-100 rounded-lg md:grid-cols-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-lg font-bold text-gray-900">{stat.number}</div>
                    <div className="text-xs text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>

              {/* Key Highlights */}
              <div className="p-3 mb-4 rounded-lg bg-gray-50">
                <h3 className="mb-2 text-xs font-semibold text-gray-800">Key Highlights</h3>
                <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {[
                    "Digital classrooms with smart boards",
                    "Well-equipped science, computer & Robotics labs",
                    "Spacious library with 10,000+ books",
                    "Indoor and outdoor sports facilities",
                    "Music, dance, and art studios",
                    "Regular workshops and seminars"
                  ].map((highlight, index) => (
                    <div key={index} className="flex items-start gap-1.5">
                      <FaCheckCircle className="w-3 h-3 mt-0.5 text-green-500 flex-shrink-0" />
                      <span className="text-xs leading-tight text-gray-600">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;